// src/native-bridge.js
// CORRECTED: Uses PhotoGallery plugin (custom gallery) instead of PhotoPickerPlugin (system picker)
// - Android: NativeExif + GalleryActivity (unchanged)
// - iOS: PhotoGallery + PhotoGalleryViewController (custom UI matching Android)

import { Capacitor, registerPlugin } from '@capacitor/core';
import { Camera } from '@capacitor/camera';
import { NativeGeocoder } from '@capgo/nativegeocoder';

/* -------------------- Platform helpers -------------------- */
const isNative = () => (Capacitor.isNativePlatform ? Capacitor.isNativePlatform() : false);
const platform = () => (Capacitor.getPlatform ? Capacitor.getPlatform() : 'web');
export const isNativeAndroid = () => (platform() === 'android' && isNative());
export const isNativeIOS = () => (platform() === 'ios' && isNative());

// iOS-only native reverse geocode (keeps Android/web unchanged)
export async function reverseGeocodeNativeIOS(lat, lng) {
  if (!isNativeIOS()) return null;

  try {
    const result = await NativeGeocoder.reverseGeocode({
      latitude: lat,
      longitude: lng,
      useLocale: true,
      maxResults: 1,
    });

    const a = result?.addresses?.[0];
    if (!a) return null;

    // Normalize to the "address" shape App.js already expects:
    // App.js reads: payload.city || payload.town || payload.village
    return {
      city: a.locality || '',
      town: a.locality || '',
      village: a.subLocality || '',
      municipality: a.subAdministrativeArea || '',
      state: a.administrativeArea || '',
      country: a.countryName || a.countryCode || '',
      country_code: (a.countryCode || '').toLowerCase(),
      postcode: a.postalCode || '',
      road: a.thoroughfare || '',
    };
  } catch (e) {
    console.warn('[reverseGeocodeNativeIOS] failed:', e?.message || e);
    return null;
  }
}


/* -------------------- Plugins (lazy registration) -------------------- */

// Android plugin (unchanged)
const NativeExif = registerPlugin('NativeExif');

// ✅ FIXED: Use PhotoGallery (custom gallery) instead of PhotoPickerPlugin (system picker)
let PhotoGallery = null;
function getPhotoGallery() {
  if (!PhotoGallery && isNativeIOS()) {
    try {
      // This must match the name in PhotoGalleryPlugin.m: CAP_PLUGIN(..., "PhotoGallery", ...)
      PhotoGallery = registerPlugin('VivaGallery');
      console.log('[native-bridge] VivaGallery plugin registered');
    } catch (e) {
      console.error('[native-bridge] Failed to register VivaGallery:', e);
    }
  }
  return PhotoGallery;
}

/* -------------------- Utilities -------------------- */

/**
 * Request photo permissions and return the result
 */
export async function requestPhotoPermissions() {
  try {
    // iOS: Use our PhotoGallery plugin for permissions
    if (isNativeIOS()) {
      const gallery = getPhotoGallery();
      if (gallery?.checkPermissions) {
        const before = await gallery.checkPermissions();
        console.log('[permissions] iOS status:', before?.status);
        
        if (before?.status === 'granted' || before?.status === 'limited') {
          return { granted: true, canAskAgain: true };
        }
        
        const result = await gallery.requestPermissions();
        return {
          granted: result?.granted ?? false,
          canAskAgain: result?.canAskAgain ?? true
        };
      }
    }
    
    // Android/Web: Use Camera plugin
    const before = await Camera.checkPermissions();
    console.log('[permissions] Current status:', before?.photos);

    const alreadyGranted = before?.photos === 'granted' || before?.photos === 'limited';
    if (alreadyGranted) {
      return { granted: true, canAskAgain: true };
    }

    const result = await Camera.requestPermissions({ permissions: ['photos'] });
    console.log('[permissions] After request:', result?.photos);

    const granted = result?.photos === 'granted' || result?.photos === 'limited';
    let canAskAgain = true;
    if (before?.photos === 'denied' && result?.photos === 'denied') {
      canAskAgain = false;
    }

    return { granted, canAskAgain };
  } catch (e) {
    console.warn('[permissions] Request failed:', e?.message || e);
    return { granted: false, canAskAgain: true };
  }
}

async function ensurePhotoPermissions() {
  const result = await requestPhotoPermissions();
  return result.granted;
}

function toWebViewUrl(uri) {
  try {
    if (Capacitor.convertFileSrc && uri && (uri.startsWith('file://') || uri.startsWith('content://'))) {
      return Capacitor.convertFileSrc(uri);
    }
  } catch {}
  return uri;
}

function parseExifDate(exifDate) {
  if (!exifDate) return new Date().toISOString().slice(0, 10);
  try {
    const parts = String(exifDate).split(' ')[0].split(':');
    if (parts.length === 3) {
      return `${parts[0]}-${parts[1]}-${parts[2]}`;
    }
  } catch {}
  return new Date().toISOString().slice(0, 10);
}

// Android photo mapper (unchanged)
function mapNativeToPhoto(p) {
  const dateISO =
    p?.dateTaken ||
    parseExifDate(p?.dateOriginal || p?.date) ||
    new Date().toISOString().slice(0, 10);

  const hasGPS =
    typeof p?.latitude === 'number' && typeof p?.longitude === 'number';

  const fileName = p?.filename || p?.fileName || 'image';
  const raw = p?.previewUri || p?.uri || '';
  const url = toWebViewUrl(raw);

  return {
    url,
    originalFile: null,
    isHEIC: /\.heic($|\?)/i.test(fileName),
    sourceType: 'native',
    converted: false,
    likes: 0,
    gpsExtracted: hasGPS,
    fileName,
    dateTaken: dateISO,
    hasGPS,
    coordinates: hasGPS ? { lat: p.latitude, lng: p.longitude } : null,
  };
}

// iOS photo mapper (for PhotoGallery plugin output)
function mapIOSToPhoto(p) {
  const dateISO = p?.dateTaken || new Date().toISOString().slice(0, 10);
  
  const hasGPS =
    typeof p?.latitude === 'number' && typeof p?.longitude === 'number';

  const fileName = p?.filename || p?.fileName || 'image';
  const raw = p?.previewUri || p?.uri || p?.url || '';
  const url = toWebViewUrl(raw);

  return {
    url,
    originalFile: null,
    isHEIC: /\.heic($|\?)/i.test(fileName) || (p?.isHEIC === true),
    sourceType: 'native',
    converted: false,
    likes: 0,
    gpsExtracted: hasGPS,
    fileName,
    dateTaken: dateISO,
    hasGPS,
    coordinates: hasGPS ? { lat: p.latitude, lng: p.longitude } : null,
    localIdentifier: p?.localIdentifier,
  };
}

/* -------------------- MAIN ENTRY POINT -------------------- */

/**
 * Main album import function - routes to platform-specific implementation
 * 
 * Android: Opens GalleryActivity (custom MediaStore UI)
 * iOS: Opens PhotoGalleryViewController (custom PhotoKit UI)
 */
export async function tryNativeAlbumImport(opts = {}) {
  // ✅ iOS: Use PhotoGallery plugin (custom gallery UI)
  if (isNativeIOS()) {
    return await iosGalleryImport(opts);
  }

  // Android: Use existing NativeExif + GalleryActivity
  if (!isNativeAndroid()) return [];
  await ensurePhotoPermissions();

  if (!NativeExif?.openAlbum) {
    console.warn('[native-bridge] NativeExif.openAlbum not available');
    return [];
  }

  try {
    // 🔹 FIX: Only include date params if they are actual numbers
    // Passing null can cause issues with Capacitor's bridge to Kotlin
    const params = {};
    if (typeof opts.dateFromMs === 'number' && opts.dateFromMs > 0) {
      params.dateFromMs = opts.dateFromMs;
    }
    if (typeof opts.dateToMs === 'number' && opts.dateToMs > 0) {
      params.dateToMs = opts.dateToMs;
    }
    
    console.log('[native-bridge] Opening album with params:', params);
    const result = await NativeExif.openAlbum(params);

    if (result && Array.isArray(result.items)) {
      console.log(`[native-bridge] Album import: received ${result.items.length} photos`);
      return result.items.map(mapNativeToPhoto).filter(p => p && p.url);
    }

    if (Array.isArray(result)) {
      console.log(`[native-bridge] Album import: received ${result.length} photos (array format)`);
      return result.map(mapNativeToPhoto).filter(p => p && p.url);
    }

    console.warn('[native-bridge] Album import: unexpected response format', result);
    return [];
  } catch (e) {
    if (e?.message?.toLowerCase().includes('cancel')) throw e;
    console.error('[native-bridge] openAlbum failed:', e);
    throw e;
  }
}

/* -------------------- iOS Gallery Import (FIXED) -------------------- */

/**
 * ✅ FIXED: Uses PhotoGallery.openGallery() instead of PhotoPickerPlugin.pickPhotos()
 * This presents our custom PhotoGalleryViewController instead of system PHPickerViewController
 */
async function iosGalleryImport(opts = {}) {
  const gallery = getPhotoGallery();
  
  // Debug logging
  console.log('[iOS] Attempting to open custom gallery...');
  console.log('[iOS] Gallery plugin:', gallery);
  console.log('[iOS] Has openGallery?', typeof gallery?.openGallery);
  
  if (!gallery?.openGallery) {
    console.error('[iOS] PhotoGallery.openGallery not available!');
    console.error('[iOS] Make sure PhotoGalleryPlugin.swift and .m are added to Xcode project');
    return [];
  }

  // Setup progress listener
  let progressUnsub = null;
  if (gallery?.addListener) {
    try {
      progressUnsub = await gallery.addListener('photoImportProgress', (e) => {
        console.log(`[iOS progress] ${e?.current}/${e?.total} (${Math.round((e?.progress || 0) * 100)}%)`);
        
        // Dispatch custom event for UI
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('photoImportProgress', { 
            detail: { current: e?.current, total: e?.total, progress: e?.progress } 
          }));
        }
      });
    } catch (err) {
      console.warn('[iOS] addListener failed (non-fatal):', err);
    }
  }

  try {
    console.log('[iOS] Calling gallery.openGallery with opts:', opts);
    
    // ✅ Call openGallery (custom UI) instead of pickPhotos (system UI)
    const result = await gallery.openGallery({
      dateFromMs: opts.dateFromMs || null,
      dateToMs: opts.dateToMs || null,
    });

    console.log('[iOS] Gallery result:', result);
    
    // PhotoGallery returns { items: [...], count: N, completed: true }
    const items = Array.isArray(result?.items) ? result.items : [];
    console.log(`[iOS] Gallery returned ${items.length} photos`);

    // Map to standard photo format
    const photos = items.map(mapIOSToPhoto).filter(p => p && p.url);
    console.log(`[iOS] Mapped ${photos.length} valid photos`);

    return photos;
  } catch (e) {
    if (e?.message?.toLowerCase().includes('cancel')) {
      console.log('[iOS] User cancelled gallery');
      return [];
    }
    console.error('[iOS] openGallery failed:', e);
    throw e;
  } finally {
    if (progressUnsub?.remove) {
      progressUnsub.remove();
    }
  }
}

/* -------------------- Android-only helpers (unchanged) -------------------- */

export async function tryNativePickAndBuildPhotos() {
  if (!isNativeAndroid()) return [];
  await ensurePhotoPermissions();

  if (!NativeExif?.pickAndExtract) {
    console.warn('[native-bridge] NativeExif.pickAndExtract not available');
    return [];
  }

  try {
    const out = await NativeExif.pickAndExtract();

    if (Array.isArray(out)) {
      return out.map(mapNativeToPhoto).filter(p => p && p.url);
    }
    if (out && Array.isArray(out.items)) {
      return out.items.map(mapNativeToPhoto).filter(p => p && p.url);
    }
    return [];
  } catch (e) {
    console.error('[native-bridge] pickAndExtract failed:', e);
    return [];
  }
}

export async function tryNativeFolderImport() {
  if (!isNativeAndroid()) return [];
  await ensurePhotoPermissions();

  if (!NativeExif?.pickFolderAndExtract) {
    console.warn('[native-bridge] NativeExif.pickFolderAndExtract not available');
    return [];
  }

  try {
    const out = await NativeExif.pickFolderAndExtract();

    if (Array.isArray(out)) {
      return out.map(mapNativeToPhoto).filter(p => p && p.url);
    }
    if (out && Array.isArray(out.items)) {
      return out.items.map(mapNativeToPhoto).filter(p => p && p.url);
    }
    return [];
  } catch (e) {
    console.error('[native-bridge] pickFolderAndExtract failed:', e);
    return [];
  }
}

/* -------------------- Web fallback -------------------- */

export async function useWebCamera() {
  try {
    const image = await Camera.getPhoto({
      quality: 90,
      allowEditing: false,
      resultType: 'uri',
      source: 'photos'
    });
    return [{
      url: image.webPath,
      dateTaken: new Date().toISOString().slice(0, 10),
      coordinates: null,
      thumbnail: image.webPath,
      likes: 0,
      sourceType: 'web'
    }];
  } catch (err) {
    console.error('[Camera] Error:', err);
    return [];
  }
}